/**
 * @component LanguageSwitcher
 * @description 語言切換下拉選單（UI-8-1）
 * 支援 zh-TW / zh-CN / en 三語言切換
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGE_OPTIONS = [
  { code: 'zh-TW', label: '繁體中文', flag: '🇹🇼' },
  { code: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
] as const;

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsId = useId();

  useEffect(() => {
    if (!open) return;
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, [open]);

  const handleChange = (lang: string) => {
    setOpen(false);
    if (i18n.language === lang) {
      triggerRef.current?.focus();
      return;
    }
    try {
      localStorage.setItem('language', lang);
    } catch {
      /* Safari private mode */
    }
    // 整頁重整確保所有元件（含持久 Sidebar）都以新語言重新渲染
    window.location.reload();
  };

  const current = LANGUAGE_OPTIONS.find((l) => l.code === i18n.language) ?? LANGUAGE_OPTIONS[0];

  return (
    <div
      ref={containerRef}
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                   text-base-content/60 hover:text-base-content
                   hover:bg-base-300 transition-colors"
        ref={triggerRef}
        aria-label={t('switchLanguage')}
        aria-expanded={open}
        aria-controls={optionsId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{current.code === 'en' ? 'EN' : current.code === 'zh-TW' ? '繁中' : '简中'}</span>
        <span className="hidden sm:inline">{current.label}</span>
        <svg className="w-4 h-4 opacity-60" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Dropdown */}
      <div
        id={optionsId}
        hidden={!open}
        className="absolute right-0 mt-1 w-40 rounded-md shadow-lg
                      bg-base-200 border border-base-300
                      z-50"
      >
        {LANGUAGE_OPTIONS.map((lang) => (
          <button
            key={lang.code}
            type="button"
            onClick={() => handleChange(lang.code)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                        hover:bg-primary/10 transition-colors
                        ${i18n.language === lang.code ? 'text-primary font-medium' : 'text-base-content'}`}
          >
            <span>{lang.label}</span>
            {i18n.language === lang.code && (
              <svg className="ml-auto w-4 h-4 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default LanguageSwitcher;
